import { useEffect, useState } from 'react'
import { api } from '@/lib/api'

/**
 * Live net-worth indicator in the terminal header. Hits
 * /api/net-worth/summary every 60s and shows total + tracked
 * investment value. Click to run /portfolio in the terminal.
 *
 * Shows a dash if the endpoint fails — NAV shouldn't block the terminal
 * from rendering.
 */

interface NavSummary {
  total_net_worth_gbp: number
  tracked_investments_gbp: number
}

interface LivePnlSummary {
  total_value?: number
  /** GBP change today (vs last snapshot / yesterday close) */
  today_change?: number
  today_change_pct?: number
}

const REFRESH_MS = 60_000

function fmtGBP(v: number): string {
  if (!v) return '£—'
  if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(2)}M`
  if (v >= 1_000) return `£${(v / 1_000).toFixed(0)}k`
  return `£${v.toFixed(0)}`
}

const NAV_CACHE_KEY = 'nav:last-summary'

function readCachedNav(): NavSummary | null {
  try {
    const raw = localStorage.getItem(NAV_CACHE_KEY)
    if (!raw) return null
    const v = JSON.parse(raw)
    return typeof v?.total_net_worth_gbp === 'number' ? (v as NavSummary) : null
  } catch {
    return null
  }
}

export function NavIndicator({ onClick }: { onClick?: () => void }) {
  // Seed from the last figure this browser saw so login shows the real
  // number instantly instead of "loading…" followed by a visible correction.
  const [data, setData] = useState<NavSummary | null>(readCachedNav)
  const [pnl, setPnl] = useState<LivePnlSummary | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let alive = true
    const fetchOnce = async () => {
      try {
        const [nw, livePnl] = await Promise.all([
          api.get<NavSummary>('/api/net-worth/summary'),
          api.get<LivePnlSummary>('/api/pnl/live').catch(() => ({ data: {} })),
        ])
        if (alive) {
          setData(nw.data)
          setPnl(livePnl.data ?? null)
          setError(false)
          try {
            localStorage.setItem(NAV_CACHE_KEY, JSON.stringify(nw.data))
          } catch {
            /* storage full/blocked — cache is best-effort */
          }
        }
      } catch {
        if (alive) setError(true)
      }
    }
    fetchOnce()
    const t = setInterval(fetchOnce, REFRESH_MS)
    return () => {
      alive = false
      clearInterval(t)
    }
  }, [])

  if (error && !data) {
    return (
      <span className="text-crt-dim font-mono text-xs" title="NAV unavailable">
        NAV —
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={
        data
          ? `Total net worth: £${data.total_net_worth_gbp.toLocaleString()}\n` +
            `Tracked investments: £${data.tracked_investments_gbp.toLocaleString()}\n` +
            (pnl?.today_change != null
              ? `Today: ${pnl.today_change >= 0 ? '+' : ''}£${pnl.today_change.toFixed(0)}\n`
              : '') +
            'Click to run /portfolio'
          : 'Loading NAV…'
      }
      className="text-crt-dim hover:text-crt-green font-mono text-xs transition-colors flex items-center gap-1"
    >
      <span className="text-crt-dim">NAV</span>
      {data ? (
        <>
          <span className="text-crt-green font-bold">{fmtGBP(data.total_net_worth_gbp)}</span>
          {pnl?.today_change != null && pnl?.today_change_pct != null ? (
            <span
              className={
                pnl.today_change >= 0
                  ? 'text-crt-green text-[10px]'
                  : 'text-red-400 text-[10px]'
              }
              title={`Today: ${pnl.today_change >= 0 ? '+' : ''}£${pnl.today_change.toFixed(0)}`}
            >
              {pnl.today_change >= 0 ? '▲' : '▼'}
              {Math.abs(pnl.today_change_pct).toFixed(2)}%
            </span>
          ) : (
            <span className="text-crt-dim/70 text-[10px]">
              ({fmtGBP(data.tracked_investments_gbp)} invested)
            </span>
          )}
        </>
      ) : (
        <span className="text-crt-dim">loading…</span>
      )}
    </button>
  )
}
